import React, { useEffect } from "react";
import { FaMailBulk, FaWhatsapp, FaArrowRight } from "react-icons/fa";
import TextEdit from "../../molecules/TextEdit";

import { useSignal } from "@preact/signals-react";
import useFetch from "../../../hooks/useFetch";
import { Variable } from "./BatchNotifcationForm";
import ErrorMessage from "../../atoms/ErrorMessage";
import { toast } from "react-toastify";

const NOTIFICATION_URL = `${process.env.REACT_APP_COMMUNICATION_API_URL}/notifications`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

enum MediaType {
    WPP = "WHATSAPP",
    MAIL = "EMAIL",
}

type Notification = {
    type_scheduled: string;
    operation: string;
    maxAttempts: number;
    media: MediaType;
    status: string;
    createdAt: string;
    sentAt: string;
    externalId: string;
    contactName: string;
    isPhoneValid: boolean;
    isEmailValid: boolean;
    phone: string;
    email: string;
    cc: string | null;
    bcc: string | null;
    attempts: number;
    errorMessage: string;
    variables: Variable[];
    templateName: string;
    canDispatch: boolean;
    subject?: string; // Optional for email notifications
    from?: string; // Optional for email notifications
    replyTo?: string; // Optional for email notifications
}

interface NotificationPageViewProps {
    typeScheduled: string;
    targetDate: string;
    operation: string;
    onClose?: () => void;
}

const NotificationPageView: React.FC<NotificationPageViewProps> = ({
    typeScheduled,
    targetDate,
    operation,
    onClose
}) => {
    const id = `${typeScheduled}:${targetDate}:${operation}`;

    const apiVersion = useSignal('');
    const isLoadingForm = useSignal(false);
    const notification = useSignal<Notification>({
        type_scheduled: "",
        operation: "",
        maxAttempts: 0,
        media: MediaType.WPP,
        status: "",
        createdAt: "",
        sentAt: "",
        externalId: "",
        contactName: "",
        isPhoneValid: false,
        isEmailValid: false,
        phone: "",
        email: "",
        cc: null,
        bcc: null,
        attempts: 0,
        errorMessage: "",
        variables: [],
        templateName: "",
        canDispatch: false,
    });

    const { isLoading, data, error, isTokenError } = useFetch(`${NOTIFICATION_URL}/${id}/page-view`, [{ key: 'x-api-key', value: API_KEY }]);

    // TODO not found

    useEffect(() => {
        if (isTokenError && onClose) {
            onClose();
        }
        if (error && onClose) {
            onClose();
        }
    }, [isTokenError, error, onClose]);

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;

            const { template } = data;
            const variables: Variable[] = [];
            template.variables.forEach((variable: any) => {
                if (variable.format !== "email" && variable.format !== "phone" && variable.name !== "FULL_NAME") {
                    const value = data.notification.variables[variable.field] || "";

                    variables.push({
                        field: variable.display,
                        value: value,
                        format: variable.format,
                    });
                }
            });

            let subject = "";
            let from = "";
            let replyTo = "";

            if (data.notification.media === "EMAIL") {
                const [innerTemplate] = template.templates;
                subject = innerTemplate.subject.replace(/{{(.*?)}}/g, (_: string, key: string): string => {
                    const variable = (template.variables.find((v: any) => v.name === key)) as Variable;
                    return data.notification.variables[variable.field] || "";
                });

                from = innerTemplate.from;
                replyTo = innerTemplate.replyTo;
            }

            notification.value = {
                ...notification.value,
                type_scheduled: data.notification.type_scheduled,
                operation: data.notification.operation,
                maxAttempts: data.notification.maxAttempts,
                media: data.notification.media,
                status: data.notification.status,
                createdAt: data.notification.createdAt,
                sentAt: data.notification.sentAt,
                externalId: data.notification.externalId,
                contactName: data.notification.contactName,
                isPhoneValid: data.notification.isPhoneValid,
                isEmailValid: data.notification.isEmailValid,
                phone: data.notification.phone,
                email: data.notification.email,
                attempts: data.notification.attempts,
                errorMessage: data.notification.errorMessage,
                variables: variables,
                templateName: template.description,
                canDispatch: data.notification.canDispatch,
                subject,
                from,
                replyTo,
            };
        }
    }, [data]);

    //TODO mover para libs ou componente especifico
    const getContentByStatus = (status: string) => {
        const statusTable = {
            "PENDING": "Notificação pendente",
            "CHECKING": "Checando status da notificação",
            "SENT": "Notificação enviada",
            "ERROR": "Erro ao enviar notificação",
            "RECEIVED": "Notificação recebida",
            "DELIVERED": "Notificação entregue",
            "FAILED": "Falha ao enviar notificação",
            "CONSUMED": "Visualizado pelo destinatário",
        };

        return statusTable[status as keyof typeof statusTable] || "Status desconhecido";
    };

    const getCssColorBy = (status: string) => {

        const statusTable = {
            "PENDING": "bg-slate-400 dark:bg-slate-600",
            "CHECKING": "bg-slate-500 dark:bg-slate-700",
            "SENT": "bg-green-500 dark:bg-green-700",
            "ERROR": "bg-red-500 dark:bg-red-700",
            "RECEIVED": "bg-blue-500 dark:bg-blue-700",
            "DELIVERED": "bg-blue-500 dark:bg-blue-700",
            "CONSUMED": "bg-green-700 dark:bg-green-900",
            "FAILED": "bg-red-600 dark:bg-red-800",
        };

        return statusTable[status as keyof typeof statusTable] || "bg-gray-500 dark:bg-gray-700";
    };

    const dispatch = async () => {
        isLoadingForm.value = true;
        try {
            const response = await fetch(`${process.env.REACT_APP_COMMUNICATION_API_URL}/dispatch`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    notificationType: typeScheduled,
                    targetDate,
                    service: '',
                    operations: [notification.value.operation],
                }),
            });
            if (response.ok) {
                toast.success('Notificação iniciada com sucesso!');
                onClose?.();
            } else {
                toast.error('Erro ao iniciar notificação!');
            }
        } catch (error) {
            toast.error('Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.');
        }
        isLoadingForm.value = false;
    };

    return (
        <div className="p-3 bg-white dark:bg-slate-800 rounded-lg h-full overflow-y-auto">
            {isLoading ? (
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center space-y-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">Carregando detalhes...</span>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between mb-4 ml-2">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {notification.value.contactName}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                #{notification.value.operation}
                            </p>
                        </div>
                        {notification.value.canDispatch && (
                            <button
                                disabled={isLoadingForm.value}
                                className="flex items-center text-white bg-[#FF9119] hover:bg-[#FF9119]/80 font-medium rounded text-sm px-3 py-2"
                                onClick={dispatch}
                            >
                                Notificar
                                <FaArrowRight className="ml-2" />
                            </button>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Tentativas: {notification.value.attempts}/{notification.value.maxAttempts}
                                </span>
                                {notification.value.media === MediaType.WPP && (
                                    <FaWhatsapp className="text-xl text-green-700" />
                                )}
                                {notification.value.media === MediaType.MAIL && (
                                    <FaMailBulk className="text-xl text-blue-700" />
                                )}
                            </div>
                            <span className={`${getCssColorBy(notification.value.status)} text-white text-xs font-medium px-2 py-1 rounded-sm`}>
                                {notification.value.status}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {getContentByStatus(notification.value.status)}
                            </p>
                        </div>

                        {notification.value.errorMessage && (
                            <ErrorMessage message={notification.value.errorMessage} />
                        )}

                        <div className="space-y-3">
                            <TextEdit label="Tipo:" value={notification.value.templateName} disabled />
                            <TextEdit label="Data:" value={targetDate?.split("-").reverse().join("/")} disabled />

                            {notification.value.media === MediaType.WPP && (
                                <TextEdit label="Telefone:" value={notification.value.phone} disabled prefix="+55" />
                            )}

                            {notification.value.media === MediaType.MAIL && (
                                <>
                                    <TextEdit label="Email:" value={notification.value.email} disabled />
                                    <TextEdit label="Assunto:" value={notification.value.subject} disabled />
                                    <TextEdit label="De:" value={notification.value.from} disabled />
                                    {notification.value.replyTo && (
                                        <TextEdit label="Responder:" value={notification.value.replyTo} disabled />
                                    )}
                                    {notification.value.cc && (
                                        <TextEdit label="Cc:" value={notification.value.cc} disabled />
                                    )}
                                    {notification.value.bcc && (
                                        <TextEdit label="Bcc:" value={notification.value.bcc} disabled />
                                    )}
                                </>
                            )}

                            {notification.value.variables.map((variable, index) => (
                                <TextEdit
                                    key={index}
                                    label={`${variable.field}:`}
                                    value={variable.value}
                                    disabled
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default NotificationPageView;
